import type { PlantStatus, PlantType, SunExposure } from '../types';

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Convert pixel (x, y) — (0,0) at top-left — to Leaflet [lat, lng]
// With bounds [[0,0],[h,w]], Leaflet y-axis points up, so we invert y.
export function pixelToLatLng(x: number, y: number, mapHeight: number): [number, number] {
  return [mapHeight - y, x];
}

// Convert Leaflet [lat, lng] back to pixel (x, y)
export function latLngToPixel(lat: number, lng: number, mapHeight: number): [number, number] {
  return [lng, mapHeight - lat];
}

// Parse polygon_json stored as [[x,y],...] into Leaflet positions
export function polygonToLatLngs(
  polygonJson: string,
  mapHeight: number,
): [number, number][] {
  try {
    const coords = JSON.parse(polygonJson) as [number, number][];
    return coords.map(([x, y]) => pixelToLatLng(x, y, mapHeight));
  } catch {
    return [];
  }
}

export const STATUS_COLORS: Record<PlantStatus, string> = {
  planned: '#94a3b8',
  planted: '#60a5fa',
  thriving: '#4ade80',
  struggling: '#fb923c',
  removed: '#f87171',
  dead: '#6b7280',
};

export const STATUS_LABELS: Record<PlantStatus, string> = {
  planned: 'Planned',
  planted: 'Planted',
  thriving: 'Thriving',
  struggling: 'Struggling',
  removed: 'Removed',
  dead: 'Dead',
};

export const STATUS_BG: Record<PlantStatus, string> = {
  planned: 'bg-slate-100 text-slate-700',
  planted: 'bg-blue-100 text-blue-700',
  thriving: 'bg-green-100 text-green-700',
  struggling: 'bg-orange-100 text-orange-700',
  removed: 'bg-red-100 text-red-700',
  dead: 'bg-gray-100 text-gray-600',
};

export const TYPE_LABELS: Record<PlantType, string> = {
  annual: 'Annual',
  perennial: 'Perennial',
  shrub: 'Shrub',
  tree: 'Tree',
  herb: 'Herb',
  vegetable: 'Vegetable',
  grass: 'Grass',
  bulb: 'Bulb',
  groundcover: 'Groundcover',
  other: 'Other',
};

export const TYPE_EMOJI: Record<PlantType, string> = {
  annual: '🌸',
  perennial: '🌼',
  shrub: '🌿',
  tree: '🌳',
  herb: '🌱',
  vegetable: '🥬',
  grass: '🌾',
  bulb: '🧅',
  groundcover: '🍀',
  other: '🪴',
};

export const SUN_LABELS: Record<SunExposure, string> = {
  full_sun: 'Full Sun',
  part_shade: 'Part Shade',
  full_shade: 'Full Shade',
  varies: 'Varies',
};

export const NOTE_TYPE_LABELS = {
  observation: 'Observation',
  maintenance: 'Maintenance',
  issue: 'Issue',
  bloom: 'Bloom',
  harvest: 'Harvest',
  idea: 'Idea',
};

export const NOTE_TYPE_COLORS = {
  observation: 'bg-slate-100 text-slate-700',
  maintenance: 'bg-blue-100 text-blue-700',
  issue: 'bg-red-100 text-red-700',
  bloom: 'bg-pink-100 text-pink-700',
  harvest: 'bg-amber-100 text-amber-700',
  idea: 'bg-purple-100 text-purple-700',
};

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
